// Connection singleton — network-aware. `getConnection()` always reads
// the active network from `@/lib/network/active` and rebuilds the
// Solana `Connection` whenever the network changes. There's no
// imperative `setConnectionNetwork()` — invalidation is implicit via
// `getActiveNetwork()`, which `NetworkSyncProvider` keeps in sync with
// the user's toggle.

import type { WsHealthState } from '@morfi/backend-sdk-v2/client';
import { Connection } from '@solana/web3.js';
import { makeInstrumentedHeliusFetch } from '@/lib/dev/helius-instrument';
import { getNetworkConfig, type Network } from '@/lib/network';
import { getActiveNetwork } from '@/lib/network/active';
import { logger } from '@/lib/telemetry/logger';
import { makeRpcObservabilityFetch } from '@/lib/tx-engine/rpc-observability-fetch';

let connectionInstance: Connection | null = null;
let connectionNetwork: Network | null = null;
let wsHealth: WsHealthState = 'disconnected';

const LOOPBACK_RPC_HOSTS: ReadonlySet<string> = new Set(['localhost', '127.0.0.1', '[::1]']);

/** Derive Solana's RPC PubSub endpoint. Local validators conventionally expose
 * PubSub on RPC port + 1; remote providers retain their configured port. */
export function deriveWsEndpoint(httpUrl: string): string {
  const url = new URL(httpUrl);
  if (url.protocol === 'https:') {
    url.protocol = 'wss:';
  } else if (url.protocol === 'http:') {
    url.protocol = 'ws:';
    if (LOOPBACK_RPC_HOSTS.has(url.hostname) && url.port !== '') {
      const rpcPort = Number(url.port);
      if (!Number.isInteger(rpcPort) || rpcPort < 1 || rpcPort >= 65_535) {
        throw new RangeError(`Local Solana RPC port cannot derive PubSub port: ${url.port}`);
      }
      url.port = String(rpcPort + 1);
    }
  } else {
    throw new TypeError(`Unsupported Solana RPC protocol: ${url.protocol}`);
  }
  return url.toString();
}

/** Get or create the singleton Connection. Rebuilds when the requested or
 *  currently active network has changed since the last call. */
export function getConnection(network: Network = getActiveNetwork()): Connection {
  if (connectionInstance !== null && connectionNetwork === network) {
    return connectionInstance;
  }

  // Network changed (or first call) — rebuild against the new RPC URL.
  const config = getNetworkConfig(network);
  const httpUrl = config.rpcUrl;
  const wsEndpoint = deriveWsEndpoint(httpUrl);

  // Development instrumentation records request volume. The outer fetch
  // retains terminal HTTP metadata for production failure logs without
  // recording endpoints, credentials, or request params.
  const instrumentedFetch =
    process.env.NODE_ENV !== 'production' ? makeInstrumentedHeliusFetch() : undefined;
  const rpcFetch = makeRpcObservabilityFetch(instrumentedFetch ?? fetch);
  connectionInstance = new Connection(httpUrl, {
    wsEndpoint,
    commitment: 'confirmed',
    fetch: rpcFetch,
  });
  connectionNetwork = network;
  wsHealth = 'connected';

  return connectionInstance;
}

/**
 * Pre-warm the connection by issuing a getSlot call.
 * Non-fatal: if it fails or times out, log a warning and proceed.
 */
export async function warmConnection(connection: Connection): Promise<void> {
  try {
    const timeout = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('warmConnection timeout')), 5000);
    });
    await Promise.race([connection.getSlot(), timeout]);
  } catch (e: unknown) {
    logger.warn('Connection warm-up failed (non-fatal)', {
      errorClass: e instanceof Error ? 'Error' : typeof e,
    });
  }
}

/** Clear the singleton for teardown. */
export function destroyConnection(): void {
  connectionInstance = null;
  connectionNetwork = null;
  wsHealth = 'disconnected';
}

/** Get the current WebSocket health state. */
export function getWsHealth(): WsHealthState {
  return wsHealth;
}

/** Update the WebSocket health state (used internally by confirmation module). */
export function setWsHealth(state: WsHealthState): void {
  wsHealth = state;
}
